import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { propertyAPI } from '../services/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PropertyGallery from '../components/PropertyGallery';
import { ExternalLink, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    propertyAPI
      .get(id)
      .then(setProperty)
      .catch(() => {
        toast.error('Property not found');
        setProperty(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background grain-overlay">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-6">
            <div className="h-10 w-2/3 bg-muted rounded" />
            <div className="aspect-[16/9] bg-muted rounded-3xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-background grain-overlay">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-24 text-center">
          <p className="text-muted-foreground mb-6 font-light">
            This property could not be found.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border text-foreground hover:bg-muted transition-colors"
          >
            Go home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const uniqueImages = [
    ...new Set([property.main_image, ...(property.all_images || [])].filter(Boolean)),
  ];

  const handleAirbnbClick = () => {
    if (!property.airbnb_url) {
      toast.error('airbnb link not available yet. Please try again later.');
      return;
    }
    window.open(property.airbnb_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-background grain-overlay">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-10 lg:mb-14">
          <span className="block text-xs uppercase tracking-[0.24em] text-primary font-medium mb-4">
            The Stay
          </span>
          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.1] max-w-4xl"
            data-testid="property-title"
          >
            {property.title}
          </h1>
        </div>

        {uniqueImages.length > 0 && (
          <div className="mb-14 lg:mb-20">
            <PropertyGallery images={uniqueImages} alt={property.title} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-8 space-y-8">
            {property.description && (
              <div>
                <h2 className="font-serif text-3xl text-foreground mb-5">
                  About this place
                </h2>
                <p className="text-lg text-muted-foreground font-light leading-[1.75] whitespace-pre-line">
                  {property.description}
                </p>
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 bg-card border border-border rounded-3xl p-8 space-y-5">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                  Book directly on
                </p>
                <p className="font-serif text-3xl text-foreground mt-2">airbnb</p>
              </div>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                Real-time availability, secure payments, and 24/7 support — all
                through airbnb&apos;s platform.
              </p>
              <button
                onClick={handleAirbnbClick}
                disabled={!property.airbnb_url}
                className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-full bg-primary text-primary-foreground text-sm font-medium tracking-wide transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_18px_40px_-12px_rgba(179,74,62,0.45)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                data-testid="airbnb-book-btn"
              >
                Book on airbnb <ExternalLink className="w-4 h-4" />
              </button>
              {!property.airbnb_url && (
                <p className="text-xs text-muted-foreground text-center">
                  Booking link coming soon.
                </p>
              )}
              <p className="text-xs text-muted-foreground/70 text-center font-light">
                You&apos;ll be redirected to airbnb to complete your booking.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PropertyDetail;
